import { Collapse, Form, Radio, Select, Switch, message } from 'antd';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxInputNumber } from 'components/RevxInputNumber';
import {
  UnitPriceBuyer,
  UnitPriceBuyerFixedQuantity,
  UnitPriceBuyerRange,
  UnitPriceBuyerRangeOption,
  UnitPricePricingMethod,
} from 'domains/skuDomain';
import { useState } from 'react';
// import { UnderlineText } from 'components/RevxTypography';
import { useMount } from 'ahooks';
import { FormListOperation } from 'antd/es/form/FormList';
import { RevxMoney } from 'components/RevxMoney';
import { RevxSpin } from 'components/RevxSpin';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import { formatNumberToThousandths } from 'utils/number';
import { OptionsValueType, getOptionsList } from './SupplierPrice';
import { LockFilled, UnlockOutlined } from '@ant-design/icons';
// import { FileDeleteIcon } from 'components/Icons';
import { FileDeleteIcon } from 'components/Icons';
import { useDebounceFn } from 'ahooks';
import { UnderlineText } from 'components/RevxTypography';
import { DEBOUNCE_TIME, NOT_APPLICABLE } from 'utils/constants';
import { PriceProps } from './UnitPrice';

enum markupEnum {
  markup = 'markup',
  markupValue = 'markupValue',
  priceEach = 'priceEach',
}

enum rangeTypeEnum {
  high = 'high',
  low = 'low',
}

type AutoSplitQuantity = {
  index: number;
  level: rangeTypeEnum;
  value: number;
};

export const BuyerPrice = ({ skuId, header, isPart, footer }: PriceProps) => {
  const [pricingMethod, setPricingMethod] = useState<UnitPricePricingMethod>(
    UnitPricePricingMethod.QUANTITY_RANGE
  );
  const [allLocked, setAllLocked] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [showRanges, setShowRanges] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [supplierOptions, setSupplierOptions] = useState<OptionsValueType[]>(
    []
  );
  const { t } = useTranslation();
  const [buyerUnitPriceForm] = Form.useForm();
  let addHandler: FormListOperation['add'],
    removeHandler: FormListOperation['remove'];
  const initRange = (highest: number, lowest: number): UnitPriceBuyerRange => {
    return {
      highest,
      lowest,
      markup: 0,
      option: UnitPriceBuyerRangeOption.NO_ADDITIONAL,
      priceEach: 0,
      supplierOrgIds: [],
      priceEachFromSupplierPrice: 0,
      markupValue: 0,
    };
  };

  const initFixedQuantity = (
    quantity: number,
    supplierOrgIds: number[] = []
  ): UnitPriceBuyerFixedQuantity => {
    return {
      quantity,
      markup: 0,
      option: UnitPriceBuyerRangeOption.NO_ADDITIONAL,
      priceEach: 0,
      supplierOrgIds,
      priceEachFromSupplierPrice: 0,
      markupValue: 0,
      previousPrice: undefined as any,
      previousPriceMarkup: undefined as any,
      previousPriceDiff: undefined as any,
    };
  };

  useMount(async () => {
    setLoading(true);
    const list = await skuService.getUnitPriceSupplierList(skuId, false);
    setSupplierOptions(getOptionsList(list));
    const data = await skuService.getBuyerUnitPrice(skuId);
    if (data) {
      if (data.pricingMethod) {
        setPricingMethod(data.pricingMethod);
        if (data.pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY) {
          /* the supplier of each line is the preferred supplier picked on the supplier price screen,
             this screen only shows it - lines saved before that keep the suppliers they were given */
          if (data.fixedQuantity?.length === 0) {
            data.fixedQuantity.push(initFixedQuantity(0));
          }
        } else if (
          data.pricingMethod === UnitPricePricingMethod.QUANTITY_RANGE
        ) {
          if (!data.quantityRanges || data.quantityRanges.length === 0) {
            data.quantityRanges = [initRange(data.maximumQuantity || 10000, 1)];
          }
        }
      }
      buyerUnitPriceForm.setFieldsValue(data);
    }
    setShowRanges(!!data);
    setLoading(false);
  });

  const {
    run: runUpdateBuyerPriceHandler,
    cancel: cancelUpdateBuyerPriceHandler,
  } = useDebounceFn(
    async () => {
      await updateBuyerPrice(false);
    },
    { wait: DEBOUNCE_TIME + 200 }
  );

  const { run: runAutoSplitQuantity, cancel: cancelAutoSplitQuantity } =
    useDebounceFn(
      ({ index, level, value }: AutoSplitQuantity) => {
        autoSplitQuantity({ index, level, value });
      },
      { wait: DEBOUNCE_TIME }
    );

  const updateBuyerPrice = async (showError: boolean = true) => {
    try {
      setLoading(true);
      const d = buyerUnitPriceForm.getFieldsValue();
      let validateFlag = true;
      let errorMsg = '';
      if (pricingMethod !== UnitPricePricingMethod.FIXED_QUANTITY) {
        d.quantityRanges.forEach((i: UnitPriceBuyerRange) => {
          if (i.supplierOrgIds.length < 1) {
            validateFlag = false;
            errorMsg = 'Please select suppliers';
          }
        });
      }
      if (!validateFlag) {
        if (showError && errorMsg) {
          message.error(errorMsg);
        }
        return;
      }
      const updateData = await skuService.updateBuyerUnitPrice(
        skuId,
        { ...d, pricingMethod },
        showError
      );
      if (updateData.fixedQuantity) {
        // No longer converting null to undefined to ensure antd clears the fields properly.
      }
      buyerUnitPriceForm.setFieldsValue(updateData);
      setIsDirty(false);
    } finally {
      setLoading(false);
    }
  };

  const renderCollapseHeader = (key: number) => {
    return (
      <RevxFlexLayout
        className="panel-header"
        justify="space-between"
        key={key}
      >
        <RevxFlexLayout className="panel-header__main">
          {/*<div className="mr-100 bold">Select suppliers</div>*/}
          <Form.Item
            name={[key, 'supplierOrgIds']}
            label="Select suppliers"
            rules={[{ required: true }]}
          >
            <Select mode="multiple" options={supplierOptions} />
          </Form.Item>
        </RevxFlexLayout>
      </RevxFlexLayout>
    );
  };

  const markupCalc = (n: number, k: markupEnum, rangeIndex: number) => {
    const allValues = buyerUnitPriceForm.getFieldsValue();
    const ranges = allValues.quantityRanges;
    const curData = ranges?.[rangeIndex] as UnitPriceBuyerRange;
    if (!curData) return;
    let markup = curData.markup,
      markupValue = curData.markupValue,
      priceEach = curData.priceEach,
      pricingMarkup = 0;
    if (k === markupEnum.priceEach) {
      priceEach = n;
      markupValue = +((priceEach * markup) / 100).toFixed(2);
    } else if (k === markupEnum.markup) {
      markup = n;
      markupValue = +((markup / 100) * curData.priceEach).toFixed(2);
    } else {
      markupValue = n;
      markup = +((markupValue / priceEach) * 100).toFixed(2);
    }
    pricingMarkup = priceEach + markupValue || 0;
    ranges[rangeIndex] = {
      ...curData,
      ...{ priceEach, markup, markupValue, pricingMarkup },
    };
    buyerUnitPriceForm.setFieldsValue({ quantityRanges: ranges });
  };

  /* the supplier price screen decides which supplier prices a quantity, this screen only names it */
  const supplierNamesOf = (supplierOrgIds?: number[]) =>
    (supplierOrgIds || [])
      .map(
        (orgId) =>
          supplierOptions.find((option) => option.value === orgId)?.label ||
          orgId
      )
      .join(' | ');

  const roundTo = (n: number, digits: number) => +(n || 0).toFixed(digits);

  /* storefront price = supplier price plus the markup/markdown on the supplier price */
  const storefrontPriceOf = (row: UnitPriceBuyerFixedQuantity) =>
    roundTo(
      (row.priceEachFromSupplierPrice || 0) * (1 + (row.markup || 0) / 100),
      6
    );

  const syncPreviousPrice = (row: UnitPriceBuyerFixedQuantity) => {
    const storefront = row.priceEach || 0;
    const previousPrice = row.previousPrice;
    if (previousPrice == null || isNaN(previousPrice)) {
      row.previousPrice = null;
      row.previousPriceMarkup = null;
      row.previousPriceDiff = null;
      return;
    }
    row.previousPriceDiff = roundTo(storefront - previousPrice, 6);
    // what % was added on the storefront price to get the previous price,
    // kept unsigned like the value typed in the column, the sign lives in the difference
    row.previousPriceMarkup = storefront
      ? roundTo(Math.abs(((previousPrice - storefront) / storefront) * 100), 2)
      : 0;
  };

  /* locked shares the option and the markup with every line, each line still
     gets its own storefront price out of its own supplier price. */
  const fixedQuantityCalc = (
    n: number,
    k: markupEnum.markup | markupEnum.priceEach,
    rowIndex: number
  ) => {
    const rows = buyerUnitPriceForm.getFieldValue(
      'fixedQuantity'
    ) as UnitPriceBuyerFixedQuantity[];
    const curRow = rows?.[rowIndex];
    if (!curRow) return;
    const applyMarkup = (row: UnitPriceBuyerFixedQuantity) => {
      // without a supplier price for the quantity there is nothing to mark up,
      // the storefront price stays as it was typed in
      if (row.priceEachFromSupplierPrice == null) return;
      row.priceEach = storefrontPriceOf(row);
      row.markupValue = roundTo(
        row.priceEach - row.priceEachFromSupplierPrice,
        6
      );
      syncPreviousPrice(row);
    };
    if (k === markupEnum.markup) {
      curRow.markup = n || 0;
      applyMarkup(curRow);
      if (allLocked) {
        rows.forEach((row, index) => {
          if (index === rowIndex) return;
          row.markup = curRow.markup;
          // a line that is not on markup/markdown keeps its own storefront price
          if (row.option === UnitPriceBuyerRangeOption.MARKUP_MARKDOWN) {
            applyMarkup(row);
          }
        });
      }
    } else {
      // the storefront price was typed in: keep the markup in sync with it
      const supplierPrice = curRow.priceEachFromSupplierPrice || 0;
      curRow.priceEach = n || 0;
      curRow.markupValue = roundTo(curRow.priceEach - supplierPrice, 6);
      if (
        curRow.option === UnitPriceBuyerRangeOption.MARKUP_MARKDOWN &&
        supplierPrice
      ) {
        curRow.markup = roundTo(
          ((curRow.priceEach - supplierPrice) / supplierPrice) * 100,
          2
        );
      }
      syncPreviousPrice(curRow);
    }
    buyerUnitPriceForm.setFieldsValue({ fixedQuantity: rows });
  };

  const autoSplitQuantity = ({ index, level, value }: AutoSplitQuantity) => {
    const x = (
      { index, level, value }: AutoSplitQuantity,
      d?: UnitPriceBuyer
    ): UnitPriceBuyer => {
      const allData =
        d || (buyerUnitPriceForm.getFieldsValue() as UnitPriceBuyer);
      const { maximumQuantity: maxQ, quantityRanges: ranges } = allData;
      const qLen = ranges.length - 1,
        curQ = ranges[index],
        preQ = ranges[index - 1],
        nextQ = ranges[index + 1];
      const isLastLine = qLen === index;
      const insertIndex = index + 1;
      const { low, high } = rangeTypeEnum;
      const isHighLevel = level === high;
      const isLowLevel = level === low;
      if (isHighLevel) {
        curQ.highest = value;
      } else if (isLowLevel) {
        curQ.lowest = value;
      }
      // console.log(index, level, value);
      // console.log('current q', curQ);
      if (isLowLevel) {
        if (index === 0) {
          ranges[0].lowest = 1;
        } else {
          if (curQ.lowest >= curQ.highest) {
            curQ.lowest = curQ.highest - 1;
            x(
              { index: index - 1, level: high, value: curQ.lowest - 1 },
              allData
            );
          } else {
            if (curQ.lowest <= 1) {
              ranges.splice(0, index);
            } else if (curQ.lowest > preQ.highest) {
              ranges.splice(
                index,
                0,
                initRange(curQ.lowest - 1, preQ.highest + 1)
              );
            } else {
              let highQIndex = 0,
                oldQIndex = index;
              while (oldQIndex > 0) {
                --oldQIndex;
                if (curQ.lowest > ranges[oldQIndex].highest) {
                  highQIndex = oldQIndex;
                  oldQIndex = 0;
                }
              }
              ranges.splice(highQIndex + 1, index - highQIndex - 1);
              ranges[highQIndex].highest = curQ.lowest - 1;
            }
          }
        }
      } else if (isHighLevel) {
        const setNextRange = () =>
          x({ index: index + 1, level: low, value: curQ.highest + 1 }, allData);
        if (isLastLine) {
          if (curQ.highest >= maxQ) {
            curQ.highest = maxQ;
          } else {
            if (curQ.highest <= curQ.lowest) {
              curQ.highest = maxQ;
            } else {
              ranges.push(initRange(maxQ, curQ.highest + 1));
            }
          }
        } else {
          if (curQ.highest >= maxQ) {
            ranges.splice(insertIndex);
            ranges[ranges.length - 1].highest = maxQ;
          } else {
            if (curQ.highest <= curQ.lowest) {
              curQ.highest = curQ.lowest + 1;
              setNextRange();
            } else {
              if (curQ.highest >= nextQ.lowest) {
                // setNextRange();
                let lowQIndex = index,
                  oldQIndex = index;
                while (oldQIndex < qLen) {
                  oldQIndex++;
                  if (
                    curQ.highest > ranges[oldQIndex].lowest ||
                    curQ.highest === nextQ.highest
                  ) {
                    lowQIndex = oldQIndex;
                  }
                }
                ranges.splice(index + 1, lowQIndex - index - 1);
                ranges[index + 1].lowest = curQ.highest + 1;
              } else {
                ranges.splice(
                  insertIndex,
                  0,
                  initRange(nextQ.lowest - 1, curQ.highest + 1)
                );
              }
            }
          }
        }
      }
      return allData;
    };

    const newAllData = x({ index, level, value });
    buyerUnitPriceForm.setFieldsValue(newAllData);
  };

  const renderCollapseContent = (
    key: number,
    remove: FormListOperation['remove']
  ) => {
    const genNameField = (name: string) => [key, name];
    const curData = buyerUnitPriceForm.getFieldsValue()?.quantityRanges?.[
      key
    ] as UnitPriceBuyerRange;
    if (!curData) return <></>;
    const option = curData.option;
    const { NO_ADDITIONAL, MARKUP_MARKDOWN, PRICING_ENGINE } =
      UnitPriceBuyerRangeOption;
    const optionBlocks = {
      [NO_ADDITIONAL]: () => {
        return (
          <>
            <RevxFlexLayout className="pricing-block mr-30">
              <div className="label title">Pricing each</div>
              <div className="price bold">
                {curData.priceEachFromSupplierPrice !== null ? (
                  <RevxMoney
                    value={curData.priceEachFromSupplierPrice || 0}
                    decimalDigits={6}
                  />
                ) : (
                  NOT_APPLICABLE
                )}
              </div>
              <div className="bold">Supplier price</div>
            </RevxFlexLayout>
            <RevxFlexLayout className="input-pricing mr-30">
              <Form.Item name={genNameField('priceEach')} label="Price each">
                <RevxInputNumber precision={6} prefix={'$'} min={0} />
              </Form.Item>
              <div className="bold">For storefront</div>
            </RevxFlexLayout>
          </>
        );
      },
      [MARKUP_MARKDOWN]: () => {
        return (
          <>
            <RevxFlexLayout className="pricing-block mr-30">
              <div className="label title">Pricing price</div>
              <div className="price bold">
                {curData.priceEachFromSupplierPrice ? (
                  <RevxMoney
                    value={curData.priceEachFromSupplierPrice || 0}
                    decimalDigits={6}
                  />
                ) : (
                  NOT_APPLICABLE
                )}
              </div>
              <div className="bold">Supplier price</div>
            </RevxFlexLayout>
            <RevxFlexLayout className="input-pricing mr-30">
              <Form.Item name={genNameField('priceEach')} label="Price each">
                <RevxInputNumber
                  min={0}
                  precision={6}
                  prefix={'$'}
                  onChange={(n) => {
                    markupCalc(n, markupEnum.priceEach, key);
                  }}
                />
              </Form.Item>
            </RevxFlexLayout>
            <RevxFlexLayout className="input-markup">
              <Form.Item name={genNameField('markup')} label="Markup">
                <RevxInputNumber
                  min={0}
                  precision={2}
                  suffix={'%'}
                  onChange={(n) => {
                    markupCalc(n, markupEnum.markup, key);
                  }}
                />
              </Form.Item>
            </RevxFlexLayout>
            <RevxFlexLayout className="input-markup-value ml-10 mr-30">
              <Form.Item
                name={genNameField('markupValue')}
                label=" "
                colon={false}
              >
                <RevxInputNumber
                  min={0}
                  precision={6}
                  prefix={'$'}
                  onChange={(n) => {
                    markupCalc(n, markupEnum.markupValue, key);
                  }}
                />
              </Form.Item>
            </RevxFlexLayout>
            <RevxFlexLayout className="pricing-block">
              <div className="label title">Pricing price/markup</div>
              <div className="price bold">
                <RevxMoney
                  value={curData.markupValue + curData.priceEach || 0}
                  decimalDigits={6}
                />
              </div>
              <div className="bold">For storefront</div>
            </RevxFlexLayout>
          </>
        );
      },
      [PRICING_ENGINE]: () => {
        return (
          <>
            <RevxFlexLayout className="input-markup">
              <Form.Item
                name={genNameField('markup')}
                label="Markup"
                rules={[{ required: true }]}
              >
                <RevxInputNumber precision={2} suffix="%" min={0} />
              </Form.Item>
            </RevxFlexLayout>
          </>
        );
      },
    };
    return (
      <RevxFlexLayout className="panel-content" key={key}>
        <RevxFlexLayout className="panel-content__main" justify="space-between">
          <RevxFlexLayout>
            <RevxFlexLayout className="quantity-block mr-30">
              <Form.Item
                name={genNameField('lowest')}
                label="Lowest Quantity"
                className="mr-24"
              >
                <RevxInputNumber
                  min={0}
                  precision={0}
                  onChange={(value) => {
                    cancelAutoSplitQuantity();
                    runAutoSplitQuantity({
                      index: key,
                      level: rangeTypeEnum.low,
                      value,
                    });
                  }}
                />
              </Form.Item>
              <Form.Item
                name={genNameField('highest')}
                label="Highest Quantity"
              >
                <RevxInputNumber
                  min={0}
                  precision={0}
                  onChange={(value) => {
                    cancelAutoSplitQuantity();
                    runAutoSplitQuantity({
                      index: key,
                      level: rangeTypeEnum.high,
                      value,
                    });
                  }}
                />
              </Form.Item>
            </RevxFlexLayout>
            <RevxFlexLayout className="quantity-options mr-30">
              <Form.Item name={genNameField('option')}>
                <Radio.Group>
                  <Radio value={UnitPriceBuyerRangeOption.NO_ADDITIONAL}>
                    {t(
                      'platform.sku.unitPrice.buyerQuantityModelRadio.noAdditional'
                    )}
                  </Radio>
                  <Radio value={UnitPriceBuyerRangeOption.MARKUP_MARKDOWN}>
                    {t('platform.sku.unitPrice.buyerQuantityModelRadio.markup')}
                  </Radio>
                  {!isPart && (
                    <Radio value={UnitPriceBuyerRangeOption.PRICING_ENGINE}>
                      {t(
                        'platform.sku.unitPrice.buyerQuantityModelRadio.pricing'
                      )}
                    </Radio>
                  )}
                </Radio.Group>
              </Form.Item>
            </RevxFlexLayout>
            {optionBlocks[option]?.()}
          </RevxFlexLayout>
          {/*<RevxFlexLayout
            className="quantity-close click"
            onClick={() => remove(key)}
          >
            <FileDeleteIcon />
          </RevxFlexLayout>*/}
        </RevxFlexLayout>
      </RevxFlexLayout>
    );
  };
  const saveBuyerPrice = () => {
    buyerUnitPriceForm.validateFields().then(async (res) => {
      await updateBuyerPrice();
    });
  };

  const previousPriceCalc = (
    val: number | null | undefined,
    field: 'previousPrice' | 'previousPriceMarkup',
    rangeIndex: number
  ) => {
    const isFixed = pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY;
    if (!isFixed) return;

    const allValues = buyerUnitPriceForm.getFieldsValue();
    const ranges = allValues.fixedQuantity;
    if (!ranges) return;
    const row = ranges[rangeIndex];
    if (!row) return;

    const storefront = row.priceEach || 0;
    let prevPrice = field === 'previousPrice' ? val : row.previousPrice;
    let markup =
      field === 'previousPriceMarkup' ? val : row.previousPriceMarkup;
    let diff: number | null | undefined = row.previousPriceDiff;

    if (val == null || (typeof val === 'number' && isNaN(val))) {
      prevPrice = null;
      markup = null;
      diff = null;
    } else {
      if (field === 'previousPrice') {
        // User entered a dollar amount for previous price
        diff = storefront - (prevPrice as number);
        // Calculate what % was added: (previousPrice - storefront) / storefront * 100
        markup = storefront
          ? (((prevPrice as number) - storefront) / storefront) * 100
          : 0;
      } else if (field === 'previousPriceMarkup') {
        // User entered a percentage — previousPrice = storefront * (1 + percentage / 100)
        markup = markup as number;
        prevPrice = storefront * (1 + markup / 100);
        diff = storefront - prevPrice;
      }
    }

    const updatedRow = {
      ...row,
      previousPrice:
        prevPrice != null && !isNaN(prevPrice as number)
          ? +(prevPrice as number).toFixed(6)
          : null,
      previousPriceMarkup:
        markup != null && !isNaN(markup as number)
          ? +Math.abs(markup as number).toFixed(2)
          : null,
      previousPriceDiff:
        diff != null && !isNaN(diff as number)
          ? +(diff as number).toFixed(6)
          : null,
    };
    ranges[rangeIndex] = updatedRow;

    buyerUnitPriceForm.setFieldsValue({ fixedQuantity: ranges });
    setIsDirty(true);
  };

  return (
    <RevxSpin spinning={loading}>
      <div className="unit-price buyer-page">
        {header()}
        <div className="unit-price__title">
          <h2>{t('platform.sku.unitPrice.buyerPrice')}</h2>
          {!showRanges ? (
            <div className="bold">
              {t('platform.sku.unitPrice.buyerPricingTips')}
            </div>
          ) : (
            <>
              <div className="bold">
                {t('platform.sku.unitPrice.buyerUnitPriceTips')}
              </div>
              <div className="bold">
                {t('platform.sku.unitPrice.buyerUnitPriceTips2')}
              </div>
            </>
          )}
        </div>
        {showRanges && (
          <>
            <Form
              form={buyerUnitPriceForm}
              requiredMark={false}
              onValuesChange={() => {
                setIsDirty(true);
              }}
              onBlur={() => {
                if (!isDirty) return;
                cancelUpdateBuyerPriceHandler();
                runUpdateBuyerPriceHandler();
              }}
            >
              {isPart && (
                <div className="unit-price__download">
                  <a href={skuService.getBuyerUnitPriceExcel(skuId)}>
                    <UnderlineText color="green">
                      Download spreadsheet
                    </UnderlineText>
                  </a>
                  <span> with supplier pricing tiers for each SKU</span>
                </div>
              )}
              <RevxFlexLayout className="mb-24">
                <RevxFlexLayout className="unit-price__quantity mr-100">
                  <Form.Item name="maximumQuantity" hidden={true}>
                    <input type="hidden" />
                  </Form.Item>
                  {t('platform.sku.unitPrice.maxQuantityLabel')}
                  {formatNumberToThousandths(
                    buyerUnitPriceForm.getFieldsValue().maximumQuantity || 0
                  )}
                </RevxFlexLayout>
                <Form.Item
                  name="sendToRfm"
                  label={t('platform.sku.unitPrice.sendToRfm')}
                >
                  <Radio.Group>
                    <Radio value={true}>{t('common.yes')}</Radio>
                    <Radio value={false}>{t('common.no')}</Radio>
                  </Radio.Group>
                </Form.Item>
              </RevxFlexLayout>
              {pricingMethod === UnitPricePricingMethod.FIXED_QUANTITY ? (
                <Form.List name="fixedQuantity">
                  {(fields, { add, remove }) => {
                    return (
                      <div className="fixed-quantity-list">
                        {/* Table Header */}
                        <RevxFlexLayout
                          className="fixed-quantity-header mb-10"
                          align="center"
                        >
                          <div style={{ width: 120, marginRight: 20 }}></div>
                          <div
                            style={{
                              width: 350,
                              marginRight: 20,
                              paddingLeft: 24,
                            }}
                          >
                            <RevxFlexLayout align="center">
                              <Switch
                                className="lock-all-lines__switch"
                                checked={allLocked}
                                onChange={setAllLocked}
                                checkedChildren={<LockFilled />}
                                unCheckedChildren={<UnlockOutlined />}
                              />
                              <div
                                className={`lock-all-lines__label${
                                  allLocked ? ' is-locked' : ''
                                }`}
                                onClick={() => setAllLocked(!allLocked)}
                              >
                                {allLocked
                                  ? 'Locked: settings apply to all lines'
                                  : 'Unlocked: each line set separately'}
                              </div>
                            </RevxFlexLayout>
                          </div>
                          <div style={{ width: 150, marginRight: 20 }}>
                            <div
                              className="title bold"
                              style={{ color: '#1890ff' }}
                            >
                              Price :
                            </div>
                            <div className="bold">Supplier Price:</div>
                          </div>
                          <div style={{ width: 150, marginRight: 20 }}>
                            <div
                              className="title bold"
                              style={{ color: '#1890ff' }}
                            >
                              Price :
                            </div>
                            <div className="bold">Storefront Price:</div>
                          </div>
                          <div style={{ width: 150, marginRight: 20 }}>
                            <div
                              className="title bold"
                              style={{ color: '#1890ff' }}
                            >
                              Previous Price:
                            </div>
                            <div className="bold">Dollar Amount</div>
                          </div>
                          <div style={{ width: 180, marginRight: 20 }}>
                            <div
                              className="title bold"
                              style={{ color: 'transparent' }}
                            >
                              &nbsp;
                            </div>
                            <div className="bold">
                              % Markup/Markdown
                              <br />
                              of Storefront Price
                            </div>
                          </div>
                          <div style={{ width: 100, marginRight: 20 }}>
                            <div className="bold">Difference:</div>
                          </div>
                          <div style={{ width: 30 }}></div>
                        </RevxFlexLayout>

                        {/* Rows */}
                        {fields.map((field, index) => {
                          // Ensure we use getFieldValue to get the latest state for rendering
                          // especially for conditional rendering of fields like 'markup'
                          const row = buyerUnitPriceForm.getFieldValue([
                            'fixedQuantity',
                            index,
                          ]);
                          return (
                            <RevxSpin spinning={loading} key={field.key}>
                              <RevxFlexLayout
                                className="fixed-quantity-row mb-24"
                                align="bottom"
                              >
                                <div style={{ width: 120, marginRight: 20 }}>
                                  <div className="mb-5">Quantity:</div>
                                  <Form.Item
                                    name={[field.name, 'quantity']}
                                    noStyle
                                    rules={[{ required: true, message: '' }]}
                                  >
                                    <RevxInputNumber precision={0} min={1} />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'supplierOrgIds']}
                                    hidden
                                  >
                                    <input />
                                  </Form.Item>
                                  <Form.Item
                                    name={[field.name, 'markupValue']}
                                    hidden
                                  >
                                    <input />
                                  </Form.Item>
                                  <Form.Item
                                    name={[
                                      field.name,
                                      'priceEachFromSupplierPrice',
                                    ]}
                                    hidden
                                  >
                                    <input />
                                  </Form.Item>
                                </div>

                                <div
                                  style={{
                                    width: 350,
                                    marginRight: 20,
                                    paddingTop: 27,
                                    paddingLeft: 24,
                                  }}
                                >
                                  <Form.Item
                                    name={[field.name, 'option']}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Radio.Group
                                      className="d-block"
                                      onChange={(e) => {
                                        // locked shares the option with every
                                        // line, unlocked keeps each line on its own
                                        const rows =
                                          buyerUnitPriceForm.getFieldValue(
                                            'fixedQuantity'
                                          );
                                        if (!rows?.[index]) return;
                                        const option = e.target.value;
                                        rows.forEach(
                                          (
                                            row: UnitPriceBuyerFixedQuantity,
                                            i: number
                                          ) => {
                                            if (allLocked || i === index) {
                                              row.option = option;
                                            }
                                          }
                                        );
                                        buyerUnitPriceForm.setFieldsValue({
                                          fixedQuantity: rows,
                                        });
                                      }}
                                    >
                                      <div className="mb-10">
                                        <Radio
                                          value={
                                            UnitPriceBuyerRangeOption.NO_ADDITIONAL
                                          }
                                        >
                                          No additional markup/markdown
                                        </Radio>
                                      </div>
                                      <div className="mb-10">
                                        <Radio
                                          value={
                                            UnitPriceBuyerRangeOption.MARKUP_MARKDOWN
                                          }
                                        >
                                          Use markup/markdown
                                        </Radio>
                                        {row?.option ===
                                          UnitPriceBuyerRangeOption.MARKUP_MARKDOWN && (
                                          <div
                                            style={{
                                              display: 'inline-block',
                                              marginLeft: 10,
                                            }}
                                          >
                                            <Form.Item
                                              name={[field.name, 'markup']}
                                              noStyle
                                            >
                                              <RevxInputNumber
                                                min={0}
                                                precision={2}
                                                suffix="%"
                                                style={{ width: 100 }}
                                                onChange={(val) => {
                                                  fixedQuantityCalc(
                                                    val,
                                                    markupEnum.markup,
                                                    index
                                                  );
                                                }}
                                              />
                                            </Form.Item>
                                          </div>
                                        )}
                                      </div>
                                      {!isPart && (
                                        <div className="mb-10">
                                          <Radio
                                            value={
                                              UnitPriceBuyerRangeOption.PRICING_ENGINE
                                            }
                                          >
                                            Pricing engine for selected
                                            suppliers
                                          </Radio>
                                        </div>
                                        /* Pricing Engine Logic not shown in screenshot, keeping simplified */
                                      )}
                                    </Radio.Group>
                                  </Form.Item>
                                </div>

                                <div
                                  style={{
                                    width: 150,
                                    marginRight: 20,
                                    paddingTop: 27,
                                  }}
                                >
                                  <div className="mb-5">
                                    {supplierNamesOf(row?.supplierOrgIds) || (
                                      <>&nbsp;</>
                                    )}
                                  </div>
                                  <div style={{ lineHeight: '32px' }}>
                                    <RevxMoney
                                      value={
                                        row?.priceEachFromSupplierPrice || 0
                                      }
                                      decimalDigits={6}
                                    />
                                  </div>
                                </div>

                                <div
                                  style={{
                                    width: 150,
                                    marginRight: 20,
                                    paddingTop: 27,
                                  }}
                                >
                                  <div className="mb-5">&nbsp;</div>
                                  <Form.Item
                                    name={[field.name, 'priceEach']}
                                    noStyle
                                  >
                                    <RevxInputNumber
                                      prefix="$"
                                      precision={6}
                                      onChange={(val) => {
                                        fixedQuantityCalc(
                                          val,
                                          markupEnum.priceEach,
                                          index
                                        );
                                      }}
                                    />
                                  </Form.Item>
                                </div>
                                <div
                                  style={{
                                    width: 150,
                                    marginRight: 20,
                                    paddingTop: 27,
                                  }}
                                >
                                  <div className="mb-5">&nbsp;</div>
                                  <Form.Item
                                    name={[field.name, 'previousPrice']}
                                    noStyle
                                  >
                                    <RevxInputNumber
                                      prefix="$"
                                      precision={6}
                                      allowEmpty
                                      onChange={(val) => {
                                        previousPriceCalc(
                                          val,
                                          'previousPrice',
                                          index
                                        );
                                      }}
                                    />
                                  </Form.Item>
                                </div>
                                <div
                                  style={{
                                    width: 180,
                                    marginRight: 20,
                                    paddingTop: 27,
                                  }}
                                >
                                  <div className="mb-5">&nbsp;</div>
                                  <Form.Item
                                    name={[field.name, 'previousPriceMarkup']}
                                    noStyle
                                  >
                                    <RevxInputNumber
                                      suffix="%"
                                      precision={2}
                                      allowEmpty
                                      onChange={(val) => {
                                        previousPriceCalc(
                                          val,
                                          'previousPriceMarkup',
                                          index
                                        );
                                      }}
                                    />
                                  </Form.Item>
                                </div>
                                <div
                                  style={{
                                    width: 100,
                                    marginRight: 20,
                                    paddingTop: 27,
                                  }}
                                >
                                  <div className="mb-5">&nbsp;</div>
                                  <div style={{ lineHeight: '32px' }}>
                                    {(row?.previousPriceDiff || 0) > 0
                                      ? '+'
                                      : ''}
                                    {row?.previousPriceDiff != null && (
                                      <RevxMoney
                                        value={row?.previousPriceDiff}
                                        decimalDigits={6}
                                      />
                                    )}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    width: 30,
                                    cursor: 'pointer',
                                    marginBottom: 5,
                                  }}
                                  onClick={() => remove(field.name)}
                                >
                                  <FileDeleteIcon />
                                </div>
                              </RevxFlexLayout>
                            </RevxSpin>
                          );
                        })}
                        <div className="mt-10">
                          <UnderlineText
                            color="green"
                            onClick={() => {
                              add(initFixedQuantity(0));
                            }}
                          >
                            +Add Quantity
                          </UnderlineText>
                        </div>
                      </div>
                    );
                  }}
                </Form.List>
              ) : (
                <Form.List name="quantityRanges">
                  {(fields, { add, remove }, meta) => {
                    if (!addHandler || !removeHandler) {
                      addHandler = add;
                      removeHandler = remove;
                    }
                    return fields.map((f) => {
                      return (
                        <Collapse
                          activeKey={[f.key]}
                          collapsible={'disabled'}
                          className="mb-24"
                          key={f.key}
                        >
                          <Collapse.Panel
                            header={renderCollapseHeader(f.key)}
                            key={f.key}
                            showArrow={false}
                          >
                            {renderCollapseContent(f.key, remove)}
                          </Collapse.Panel>
                        </Collapse>
                      );
                    });
                  }}
                </Form.List>
              )}
            </Form>
          </>
        )}
        {footer({ onSave: saveBuyerPrice })}
      </div>
    </RevxSpin>
  );
};
